<script setup lang="ts">
import { computed } from 'vue';

import PlusIcon from '@/components/base/assets/PlusIcon.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Caption from '@/components/base/typography/Caption.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import { ProductCardData } from '@/utils/productCard';

const props = defineProps<{
  selectedAddOns: { addOn: ProductCardData; quantity: number }[];
}>();

const shownAddOns = computed(() => props.selectedAddOns.slice(-4));

const emit = defineEmits(['open-modal']);
</script>

<template>
  <button
    class="flex flex-row items-center justify-between w-full gap-3 p-3 transition-colors bg-transparent border border-solid rounded-lg cursor-pointer font-proxima-nova border-neutral-200 hover:bg-nuts-neutral-100 hover:shadow-sm"
    aria-label="Add extra items to gift"
    type="button"
    @click="emit('open-modal')"
  >
    <template v-if="selectedAddOns.length">
      <div class="flex flex-col items-start grow gap-y-2">
        <BaseBodyText class="font-semibold text-black">Make it extra special</BaseBodyText>
        <ul class="text-left">
          <li v-for="{ addOn, quantity } in shownAddOns" :key="addOn?.sku">
            <SmallBodyText class="text-neutral-500">
              {{ quantity }} x {{ addOn.name }}
            </SmallBodyText>
          </li>
          <li v-if="selectedAddOns.length > 4">
            <SmallBodyText class="text-neutral-500">
              + {{ selectedAddOns.length - 4 }} more
            </SmallBodyText>
          </li>
        </ul>
        <BaseBodyText class="underline text-nuts-cyan-700">Update selections</BaseBodyText>
      </div>
      <div class="flex flex-col items-center w-32 gap-1">
        <div class="flex flex-row flex-wrap items-center justify-center w-full gap-1">
          <img
            v-for="{ addOn } in shownAddOns"
            class="object-contain bg-white"
            :class="selectedAddOns.length === 1 ? 'h-16 w-16' : 'h-12 w-12'"
            :key="addOn.sku"
            :src="addOn.imageUrl"
            :alt="addOn.name"
          />
        </div>
        <Caption v-if="selectedAddOns.length > 4" class="text-neutral-500">
          + {{ selectedAddOns.length - 4 }} more
        </Caption>
      </div>
    </template>
    <template v-else>
      <div class="flex items-center p-3 border border-dashed rounded-full border-neutral-400">
        <PlusIcon :size="20" class="text-neutral-500" />
      </div>
      <div class="text-left">
        <BaseBodyText class="font-semibold text-black">Make it extra special</BaseBodyText>
        <SmallBodyText class="text-neutral-500">
          Add chocolate, candles, and other items to make your gift extra special.
        </SmallBodyText>
      </div>
      <img src="@/assets/add-on_thumbnail.jpg" class="w-20 ml-auto" alt="" />
    </template>
  </button>
</template>
